import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { useUI } from "../../hooks/usePermission";
import "./Profile.css";
import strings from "../../locales/ro.json";
import HeaderElement from "../../components/HeaderElement/HeaderElement";
import ProfileCard from "../../components/Profile/ProfileCard/ProfileCard";
import LoadingCircle from "../../components/Global/LoadingCircle";

const Profile = () => {
  const { user } = UserAuth();
  const { uid } = useParams();
  const navigate = useNavigate();
  const { api } = useApi();
  const canManage = useUI('action.users.manage');

  const [isEditMode, setIsEditMode] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const isOwnProfile = !uid || uid === user?.email?.split("@")[0];
  const canEdit = isOwnProfile || canManage;

  useEffect(() => {
    if (!user?.email) return;

    if (isOwnProfile) {
      setProfileData(user.profile);
    } else {
      setLoadingProfile(true);
      api(`users/${uid}`, "GET")
        .then((data) => setProfileData(data))
        .catch((err) => console.error("Error fetching profile:", err))
        .finally(() => setLoadingProfile(false));
    }
  }, [user, uid, isOwnProfile]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    birthdate: "",
    oras: "",
    bio: "",
    picture_url: "",
    show_birthdate: false,
  });

  useEffect(() => {
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        phone: profileData.phone || "",
        birthdate: profileData.birthdate ? profileData.birthdate.split("T")[0] : "",
        oras: profileData.oras || "",
        bio: profileData.bio || "",
        picture_url: profileData.picture_url || "",
        show_birthdate: !!profileData.show_birthdate,
      });
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        phone: profileData.phone || "",
        birthdate: profileData.birthdate ? profileData.birthdate.split("T")[0] : "",
        oras: profileData.oras || "",
        bio: profileData.bio || "",
        picture_url: profileData.picture_url || "",
        show_birthdate: !!profileData.show_birthdate,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const identifier = uid || user.email.split("@")[0];
      const updatePromises = [];

      for (const [key, value] of Object.entries(formData)) {
        let originalValue = profileData[key];
        if (key === "show_birthdate") originalValue = !!originalValue;
        if (key === "birthdate" && originalValue) originalValue = originalValue.split("T")[0];

        if (value !== (originalValue || (typeof value === "boolean" ? false : ""))) {
          const payload = { field: key, newValue: value };
          updatePromises.push(
            api(`users/${identifier}`, "PATCH", payload)
          );
        }
      }

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }
      setIsEditMode(false);
      navigate(0);
    } catch (error) {
      console.error("Profile Update Failed:", error.message);
    }
  };

  const refreshProfile = async () => {
    try {
      const targetUid = uid || user.email.split("@")[0];
      const data = await api(`users/${targetUid}`, "GET");
      setProfileData(data);
    } catch (err) {
      console.error("Error refreshing profile:", err);
    }
  };

  if (!user || !user.email) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        {strings.profile?.loading || "Încărcare date profil..."}
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <LoadingCircle />
      </div>
    );
  }

  return (
    <div>
      <HeaderElement headerText={isOwnProfile ? strings.profile.title : profileData?.name || strings.profile.title} />
      <div className="profilePageContent">
        {isEditMode && canEdit ? (
          <form onSubmit={handleSubmit} className="edit-profile-form">
            <div className="form-group">
              <label htmlFor="picture_url">Profile Picture URL</label>
              <input
                type="text"
                id="picture_url"
                name="picture_url"
                placeholder="https://imgur.com/example.jpg"
                value={formData.picture_url}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="birthdate">Birthdate</label>
              <input type="date" id="birthdate" name="birthdate" value={formData.birthdate} onChange={handleChange} />
            </div>

            <div className="form-group form-group-checkbox">
              <input
                type="checkbox"
                id="show_birthdate"
                name="show_birthdate"
                checked={formData.show_birthdate}
                onChange={handleChange}
              />
              <label htmlFor="show_birthdate">Show birthdate on profile</label>
            </div>

            <div className="form-group">
              <label htmlFor="oras">City</label>
              <input type="text" id="oras" name="oras" value={formData.oras} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea id="bio" name="bio" value={formData.bio} onChange={handleChange} />
            </div>

            <div className="form-buttons">
              <button type="submit" className="profile-action-btn">Save</button>
              <button type="button" onClick={handleCancel} className="profile-action-btn">Cancel</button>
            </div>
          </form>
        ) : (
          <>
            <ProfileCard
              profileData={profileData}
              isOwnProfile={isOwnProfile}
              isAdmin={user.isAdmin}
              authToken={`Bearer ${user.accessToken}`}
              onRolesChanged={refreshProfile}
            />
            {canEdit && (
              <button className="profile-action-btn" style={{ marginTop: '16px' }} onClick={() => setIsEditMode(true)}>
                {isOwnProfile ? strings.profile.editBtn || 'Edit Profile' : strings.profile.editBtnAdmin || 'Editează Profil'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;